/** @format */

import React, {useContext, useState} from 'react';

import {QuoteContext} from 'frontier-quoter';
import {Button, Tabs, Tab, Radio, Banner, TextLink} from '@sttark/design-system';
import {Header} from '../header/header';

import {
    GRAPHIC_DESIGN_ORDER_TYPE_ARTWORK_EDITS,
    GRAPHIC_DESIGN_ORDER_TYPE_ARTWORK_RECREATION,
    GRAPHIC_DESIGN_ORDER_TYPE_CUSTOM_DESIGN,
    PRODUCT_TYPE_CARTON,
    PRODUCT_TYPE_LABEL,
    DESIGN_QUOTER_STEP_QUOTER
} from 'frontier-constants';
import ModalPortal from 'frontier-admin/modal/container_portal';
import {AddToCart} from 'frontier/ds/quoter/components/add_to_cart';

export default function DesignQuoterLanding() {
    const ctx = useContext(QuoteContext);
    const {order, designOrder} = ctx;
    const lastOrder = ctx.lastOrder;

    const labelImgSrc = '/assets/images/quoter/graphic_design/labels.png';
    const cartonImgSrc = '/assets/images/quoter/graphic_design/cartons.png';

    const handleDesignTypeChange = v => {
        setIsDesignTypeSelected(true);
        return ctx.handleChange({
            order: {
                graphic_design_order_details: {
                    order_id: {$set: order.id},
                    design_type_id: {$set: v}
                }
            }
        });
    };

    const handleTabChange = v => {
        setDesignProductTypeImage(v === PRODUCT_TYPE_LABEL ? labelImgSrc : cartonImgSrc);
        return ctx.handleChange({
            order: {graphic_design_order_details: {design_product_type_id: {$set: v}}}
        });
    };

    const [isDesignTypeSelected, setIsDesignTypeSelected] = useState(
        !!order.graphic_design_order_details.design_type_id
    );
    const [designProductTypeImage, setDesignProductTypeImage] = useState(labelImgSrc);

    return (
        <>
            <div>
                {ctx.showAddedToCart && (
                    <AddToCart
                        handleClose={() => ctx.handleChange({showAddedToCart: {$set: false}})}
                        order={lastOrder}
                    />
                )}
                {ctx.showSavedToAccount && (
                    <Banner>
                        Your quote has been saved.{' '}
                        <TextLink href="/account/orders/saved">View saved quotes</TextLink>
                    </Banner>
                )}
            </div>
            <div className="container mt-3 mb-8" style={{maxWidth: 960}}>
                <Header />
                <div className="row g-lg-7 mt-4">
                    <div className="col-md-6">
                        <h1 className="mt-6" style={{lineHeight: 1}}>
                            Get an Instant Design Quote Online
                        </h1>
                        <Button
                            secondary
                            large
                            label="Get Started"
                            onClick={() => (window.location = '#get-started')}
                        />
                    </div>
                    <div className="col-md-6">
                        <img
                            className="w-100 mt-4"
                            src="/assets/images/quoter/graphic_design/header.png"
                        />
                    </div>
                </div>
                <div className="row g-lg-7 mt-4">
                    <div className="col-md-12">
                        <h3 className="mt-0">Things to know before getting started.</h3>
                    </div>
                </div>
                <div className="row g-lg-7">
                    <div className="col-md-6">
                        <h5>Artwork Dimensions</h5>
                        <p>
                            We'll need to know what your artwork's final dimensions should be in
                            order to design your label(s) or carton(s) correctly. If you need
                            assistance determining the best-sized label or carton for your product,
                            we can help!
                        </p>
                        <p>
                            Check out the following resources or reach out to our{' '}
                            <a href="https://www.sttark.com/contact#contact">Customer Care Team</a>{' '}
                            by phone or Live Chat!
                        </p>
                        <p>
                            <a href="https://www.sttark.com/blog/label-basics-101-size-label-need-product">
                                What Size Label Do I Need For My Product?
                            </a>
                        </p>
                        <p>
                            <a href="https://www.sttark.com/blog/how-to-label-a-tapered-container-or-curved-surface">
                                How to Label a Tapered Container or Curved Surface
                            </a>
                        </p>
                        <p>
                            <a href="https://www.sttark.com/blog/how-to-measure-dimensions-of-a-box">
                                How To Measure Dimensions of a Box
                            </a>
                        </p>
                        <h5 className="mt-7">Vector or High Resolution Logo File</h5>
                        <p>
                            A vector or high-resolution logo file is a strong foundation for your
                            design. We accept the following file types: AI, PSD, EPS, PDF, JPG, PNG.
                        </p>
                        <p>
                            If you only have a low-resolution version of your logo (less than 300
                            ppi), we will recreate your logo to meet our print standards. However,
                            we currently do not offer logo creation or branding services at this
                            time.
                        </p>
                        <h5>Design Pricing</h5>
                        <p>
                            Our design service pricing is separate from your label or carton order.
                            Once you are ready to print your labels or cartons, please visit our{' '}
                            <a href="https://www.sttark.com/orders/custom-labels">Order Page</a> to
                            place those orders! If you need help with your order, don’t hesitate to
                            contact our{' '}
                            <a href="https://www.sttark.com/contact#contact">Customer Care</a> team.
                        </p>
                    </div>
                    <div className="col-md-6">
                        <h5>Materials</h5>
                        <p>
                            We offer a variety of materials for you to choose from, such as white
                            plastic, black vellum, metallic plastic, and more! Knowing the desired
                            material of your label or carton allows us to create your design to the
                            specifications of that material.
                        </p>
                        <p>
                            Visit our{' '}
                            <a href="https://www.sttark.com/label-materials">Label Materials</a> or{' '}
                            <a href="https://www.sttark.com/carton-materials">Carton Materials</a>{' '}
                            page if you need help deciding which material to choose.
                        </p>
                        <h5 className="mt-7">Regulatory and Warning Information</h5>
                        <p>
                            Many industries are regulated by government organizations like the FDA
                            and are required to display specific information on packaging and
                            labels. Food and Beverage, CBD, Cosmetics, and Supplements are some of
                            the many industries that have specific labeling requirements. Please
                            make sure you know what's required for your product when requesting
                            graphic design services.
                        </p>
                        <p>
                            Providing as much detail as possible about your product's applicable
                            regulatory information will help us design your label according to
                            industry/government regulations and compliance standards.
                        </p>
                        <p>
                            If you need help getting started, check out our{' '}
                            <a href="https://www.sttark.com/blog/category/requirements">
                                blog section
                            </a>{' '}
                            dedicated to requirements and regulations.
                        </p>
                    </div>
                </div>
                <div>
                    <h3 className="text-center" id="get-started">
                        What are you designing?
                    </h3>
                    <div className="d-flex justify-content-center">
                        <Tabs
                            activeTab={designOrder.design_product_type_id}
                            onChange={handleTabChange}>
                            <Tab name={PRODUCT_TYPE_LABEL} title="Labels" />
                            <Tab name={PRODUCT_TYPE_CARTON} title="Folding Carton" />
                        </Tabs>
                    </div>
                </div>
                <div className="row g-lg-7 mt-3">
                    <div className="col-md-6">
                        <img className="w-100" src={designProductTypeImage} />
                    </div>
                    <div className="col-md-6">
                        <h5 className="mt-0">Which option best fits your needs?</h5>
                        <div className="hstack align-items-start">
                            <Radio
                                label=""
                                id="edits"
                                value={GRAPHIC_DESIGN_ORDER_TYPE_ARTWORK_EDITS}
                                checked={
                                    designOrder.design_type_id ===
                                    GRAPHIC_DESIGN_ORDER_TYPE_ARTWORK_EDITS
                                }
                                onChange={handleDesignTypeChange}
                            />
                            <div className="ms-1 mt-1">
                                <h6>
                                    <label htmlFor="edits">Artwork Edits</label>
                                </h6>
                                <p>
                                    Our team can make quick changes to your design files before we
                                    print.
                                </p>
                            </div>
                        </div>
                        <div className="hstack align-items-start">
                            <Radio
                                label=""
                                id="recreation"
                                value={GRAPHIC_DESIGN_ORDER_TYPE_ARTWORK_RECREATION}
                                checked={
                                    designOrder.design_type_id ===
                                    GRAPHIC_DESIGN_ORDER_TYPE_ARTWORK_RECREATION
                                }
                                onChange={handleDesignTypeChange}
                            />
                            <div className="ms-1 mt-1">
                                <h6>
                                    <label htmlFor="recreation">
                                        Artwork Recreation -{' '}
                                        <span className="text-capitalize">{ctx.pluralProduct}</span>
                                    </label>
                                </h6>
                                <p>
                                    Only have an image or low resolution version of your{' '}
                                    {ctx.singularProduct}? No problem! We will re-create it to meet
                                    our printing standards.
                                </p>
                            </div>
                        </div>
                        <div className="hstack align-items-start">
                            <Radio
                                label=""
                                id="custom"
                                value={GRAPHIC_DESIGN_ORDER_TYPE_CUSTOM_DESIGN}
                                checked={
                                    designOrder.design_type_id ===
                                    GRAPHIC_DESIGN_ORDER_TYPE_CUSTOM_DESIGN
                                }
                                onChange={handleDesignTypeChange}
                            />
                            <div className="ms-1 mt-1">
                                <h6>
                                    <label htmlFor="custom">
                                        Custom {ctx.singularProduct} design
                                    </label>
                                </h6>
                                <p>
                                    Trust us to design a {ctx.singularProduct} that is perfect for
                                    you.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="text-center mt-8">
                    <Button
                        primary
                        large
                        label="Start Your Quote"
                        onClick={() => ctx.handleSetDesignQuoterStep(DESIGN_QUOTER_STEP_QUOTER)}
                        disabled={!isDesignTypeSelected}
                    />
                </div>
            </div>
        </>
    );
}
